/**
 * PlanningService
 *
 * Purpose: Frontend service for CRUD operations on media plans via the
 *          Planning microservice REST API
 * Architecture: Singleton service class using apiService.api (the Axios instance)
 *               directly with absolute URLs to bypass the /core-api baseURL.
 *               Proxied via vite.config.ts: /planning-api → http://planning:3002.
 *               Uses the same Axios instance as apiService so auth interceptors
 *               (JWT token injection, refresh logic) still apply.
 * Dependencies: ./api (apiService), ../components/metadata-form/types (FormData)
 * Author: Development Team
 * Created: 2026-02-15
 */

import { apiService } from './api';
import type { FormData } from '../components/metadata-form/types';

/** Base URL for all media plan API endpoints (proxied to planning microservice) */
const BASE_URL = '/planning-api/v1/mediaplan';

/** Override baseURL to prevent Axios from prepending /core-api */
const REQ_CONFIG = { baseURL: '' };

/**
 * MediaPlan — response shape from the planning API
 *
 * Purpose: Represents a media plan entity returned by the backend
 */
export interface MediaPlan {
  id: string;
  // What the caller may do to this plan, from the same rules the server applies
  // (ORCH-332): list rows and the plan detail carry it
  _authz?: { can_edit: boolean; can_delete: boolean };
  [key: string]: any;
}

/**
 * MediaPlanListResponse — paginated response from the planning API
 *
 * Purpose: Wraps a page of media plans with pagination metadata
 */
export interface MediaPlanListResponse {
  status: string;
  data: MediaPlan[];
  total_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

/**
 * GetPlansParams — query parameters for listing media plans
 *
 * Purpose: Supports pagination, search, and filtering on the list endpoint
 */
export interface GetPlansParams {
  page?: number;
  page_size?: number;
  search?: string;
}

/**
 * PlanningService — CRUD operations for media plans
 *
 * Purpose: Encapsulates all HTTP calls to the planning microservice
 * Architecture: Uses apiService.api (raw Axios instance) with absolute URLs
 *               so requests go to /planning-api instead of /core-api/planning-api.
 *               Auth interceptors from apiService still apply.
 */
class PlanningService {
  /** The Axios instance with auth interceptors (shared with apiService) */
  private get http() {
    return apiService.api;
  }

  /**
   * createPlan — creates a new media plan
   *
   * Purpose: Sends form data to create a new media plan record
   * Input: data (FormData) — form values from TemplateForm
   * Output: Promise<MediaPlan> — the created media plan with server-assigned ID
   * Exceptions: Throws on HTTP errors (4xx/5xx)
   */
  async createPlan(data: FormData): Promise<MediaPlan> {
    const body = data.metadata || data;
    const response = await this.http.post<MediaPlan>(BASE_URL, body, REQ_CONFIG);
    return response.data;
  }

  /**
   * getPlans — retrieves media plans with pagination and filtering
   *
   * Purpose: Fetches a paginated list of media plans for the current tenant
   * Input: params (GetPlansParams) — optional pagination, search, and filter parameters
   * Output: Promise<MediaPlanListResponse> — paginated response with plan data and metadata
   */
  async getPlans(params?: GetPlansParams): Promise<MediaPlanListResponse> {
    const response = await this.http.get<MediaPlanListResponse>(BASE_URL, {
      ...REQ_CONFIG,
      params,
    });
    return response.data;
  }

  /**
   * getPlan — retrieves a single media plan by ID
   *
   * Purpose: Fetches detailed data for one media plan
   * Input: id (string) — media plan identifier
   * Output: Promise<MediaPlan> — the requested media plan
   */
  async getPlan(id: string): Promise<MediaPlan> {
    const response = await this.http.get<MediaPlan>(`${BASE_URL}/${id}`, REQ_CONFIG);
    return response.data;
  }

  /**
   * updatePlan — updates an existing media plan
   *
   * Purpose: Sends updated form data to update an existing media plan
   * Input: id (string) — media plan ID, data (FormData) — updated form values
   * Output: Promise<MediaPlan> — the updated media plan
   */
  async updatePlan(id: string, data: FormData): Promise<MediaPlan> {
    const body = data.metadata || data;
    const response = await this.http.put<MediaPlan>(`${BASE_URL}/${id}`, body, REQ_CONFIG);
    return response.data;
  }

  /**
   * deletePlan — deletes a media plan
   *
   * Purpose: Removes a media plan record from the system
   * Input: id (string) — media plan identifier
   * Output: Promise<void>
   */
  async deletePlan(id: string): Promise<void> {
    await this.http.delete(`${BASE_URL}/${id}`, REQ_CONFIG);
  }
}

/** Singleton instance of PlanningService for use throughout the application */
export const planningService = new PlanningService();
export default planningService;
